import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { Phone, Lock, User, Eye, EyeOff, Zap } from 'lucide-react';

const LoginScreen = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isLogin) {
        login(phone, password);
      } else {
        if (!name.trim()) {
          throw new Error('Bitte geben Sie Ihren Namen ein.');
        }
        register(phone, password, name);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 safe-area-top safe-area-bottom">
      {/* Logo and Header */}
      <div className="text-center mb-8 animate-slide-up">
        <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-2 border-primary shadow-glow">
          <img 
            src="/logo.jpg" 
            alt="S&T Brain Trainer Logo" 
            className="w-full h-full object-cover"
          />
        </div>
        <h1 className="text-3xl font-display font-bold text-foreground mb-2">
          S&T Brain Trainer
        </h1>
        <p className="text-muted-foreground text-sm">
          Dein Weg zum Industriemeister
        </p>
      </div>

      {/* Login/Register Card */}
      <Card className="w-full max-w-sm bg-card border-border shadow-lg animate-scale-in" style={{ animationDelay: '0.1s' }}>
        <CardHeader className="text-center pb-4">
          <CardTitle className="text-xl text-foreground">
            {isLogin ? 'Willkommen zurück' : 'Konto erstellen'}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {isLogin 
              ? 'Melde dich an, um weiterzulernen' 
              : 'Registriere dich, um loszulegen'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Field (only for registration) */}
            {!isLogin && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Max Mustermann"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 bg-input border-border text-foreground placeholder:text-muted-foreground focus:ring-primary"
                  />
                </div>
              </div>
            )}

            {/* Phone Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Telefonnummer</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="tel"
                  placeholder="+49 123 456789"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-10 bg-input border-border text-foreground placeholder:text-muted-foreground focus:ring-primary"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Passwort</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-input border-border text-foreground placeholder:text-muted-foreground focus:ring-primary"
                  required
                  minLength={4}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Laden...
                </span>
              ) : (
                isLogin ? 'Einloggen' : 'Konto erstellen'
              )}
            </Button>
          </form>

          {/* Toggle Login/Register */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              {isLogin ? 'Noch kein Konto?' : 'Bereits registriert?'}
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="ml-1 text-primary hover:underline font-medium"
              >
                {isLogin ? 'Registrieren' : 'Zurück zum Login'}
              </button>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="mt-8 text-xs text-muted-foreground text-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
        Daten werden lokal auf deinem Gerät gespeichert
      </p>
    </div>
  );
};

export default LoginScreen;